<template>
  <div class="bookings-accommodation-sums">
    <v-card flat outlined class="mb-4 pa-4">
      <div class="text-h6 font-weight-medium mb-2">Sammanställning av platsavgifter för gruppbokningen</div>
      <v-divider></v-divider>
      <v-list-item two-line>
        <v-list-item-content class="flex-grow-0 overflow-visible">
          <v-list-item-title>{{ formatAmount(accommodationSums?.total_accommodation_amount) }}</v-list-item-title>
          <v-list-item-subtitle>Platsavgifter totalt</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-list-item two-line>
        <v-list-item-content>
          <v-list-item-title>{{ formatAmount(accommodationSums?.paid_accommodation_amount) }}</v-list-item-title>
          <v-list-item-subtitle>Betalt/fakturerat totalt</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-list-item two-line>
        <v-list-item-content>
          <v-list-item-title>{{ formatAmount(accommodationSums?.remaining_accommodation_amount) }}</v-list-item-title>
          <v-list-item-subtitle>Kvarstående platsavgifter totalt</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-card>

    <div
      v-for="bookingsAccommodationSum in bookingsAccommodationSums"
      :key="bookingsAccommodationSum.booking_id"
      class="mb-4 pa-4 border rounded-lg"
    >
      <div class="d-flex">
        <div class="text-subtitle-1 font-weight-medium">{{ getBookingTitle(bookingsAccommodationSum.booking_id) }}</div>
        <template v-if="getBookingIsDeleted(bookingsAccommodationSum.booking_id)">
          <v-chip
            class="ml-2"
            small
            color="error"
            text-color="white"
          >
            Avbokad
          </v-chip>
        </template>
      </div>
      <v-divider></v-divider>
      <v-list-item two-line>
        <v-list-item-content class="flex-grow-0 overflow-visible">
          <v-list-item-title>{{ formatAmount(bookingsAccommodationSum.total_accommodation_amount) }}</v-list-item-title>
          <v-list-item-subtitle>Platsavgifter</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn
            icon
            large
            @click="openAccommodationChargesDialog(bookingsAccommodationSum.booking_id)"
          >
            <v-icon color="secondary">
              mdi-calendar-month
            </v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
      <v-list-item two-line>
        <v-list-item-content>
          <v-list-item-title>{{ formatAmount(bookingsAccommodationSum.paid_accommodation_amount) }}</v-list-item-title>
          <v-list-item-subtitle>Betalt/fakturerat</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-list-item two-line>
        <v-list-item-content>
          <v-list-item-title>{{ formatAmount(bookingsAccommodationSum.remaining_accommodation_amount) }}</v-list-item-title>
          <v-list-item-subtitle>Kvarstående platsavgifter</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </div>

    <accommodation-charges-dialog
      :booking="accommodationChargesDialog.booking"
      :show="accommodationChargesDialog.show"
      @close="closeAccommodationChargesDialog"
      :loading="accommodationChargesDialog.isFetchingCalendarItems"
      :calendar-items="accommodationChargesDialog.accommodationChargesCalendarItems"
      :accommodation-sums="accommodationChargesDialogAccommodationSums"
    ></accommodation-charges-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import BookingService from '@/services/BookingService.js'

import AccommodationChargesDialog from '@/components/booking/AccommodationChargesDialog.vue'

export default {
  name: 'BookingGroupBookingsAccommodationSums',
  components: {
    AccommodationChargesDialog
  },
  props: {
    bookingsAccommodationSums: {
      type: Array
    },
    accommodationSums: {
      type: Object
    },
    bookings: {
      type: Array
    },
    bookingGroup: {
      type: Object
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    accommodationChargesDialog: {
      show: false,
      isFetchingCalendarItems: false,
      booking: null,
      accommodationChargesCalendarItems: []
    }
  }),
  computed: {
    ...mapState(
      {
        triggerAccommodationChargesUpdated: state => state.booking.triggerAccommodationChargesUpdated
      }
    ),
    isMobile: function () {
      return this.$vuetify.breakpoint.smAndDown
    },
    accommodationChargesDialogAccommodationSums: function () {
      if (!this.accommodationChargesDialog.booking) {
        return null
      }
      return this.bookingsAccommodationSums.find(bas => bas.booking_id === this.accommodationChargesDialog.booking.id)
    }
  },
  methods: {
    getBookingTitle: function (bookingId) {
      if (!bookingId) {
        return 'Avgifter på gruppbokningen'
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return `Bokning #${bookingId}`
      }
      if (!this.bookingGroup?.hasMultipleCustomers) {
        // Behöver inte skriva ut kundnamnet om alla bokningar har samma kund
        return `${booking.booking_number}, ${booking.position.full_name}`
      }
      return `${booking.booking_number}, ${booking.position.full_name}, ${booking.customer.full_name}`
    },
    getBookingIsDeleted: function (bookingId) {
      if (!bookingId) {
        return false
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return false
      }
      return !!booking.deleted_at
    },
    formatAmount: function (amount) {
      if (amount === null || amount === undefined) {
        return 'N/A'
      }
      return window.conversion.toHumanReadableAmount(amount / 100)
    },
    openAccommodationChargesDialog: function (bookingId) {
      this.accommodationChargesDialog.booking = this.bookings.find(b => b.id === bookingId)
      this.accommodationChargesDialog.accommodationChargesCalendarItems = []
      this.accommodationChargesDialog.show = true
      this.getCalendarItems(bookingId)
    },
    getCalendarItems: function (bookingId) {
      this.accommodationChargesDialog.isFetchingCalendarItems = true
      BookingService.getAccommodationChargesCalendarItems(bookingId)
        .then(({ data }) => {
          if (data.status === 'success') {
            window.enrich.enrichCalendarItem(data.data.calendar_items)
            this.accommodationChargesDialog.accommodationChargesCalendarItems = data.data.calendar_items
          }
        })
        .finally(() => {
          this.accommodationChargesDialog.isFetchingCalendarItems = false
        })
    },
    closeAccommodationChargesDialog: function () {
      this.accommodationChargesDialog.show = false
    }
  },
  watch: {
    triggerAccommodationChargesUpdated: function () {
      // Direka ändringar i accommodation charges triggar denna
      if (this.accommodationChargesDialog.show && this.accommodationChargesDialog.booking && this.accommodationChargesDialog.booking.id) {
        const bookingId = this.accommodationChargesDialog.booking.id
        this.getCalendarItems(bookingId)
      }
    }
  }
}
</script>
